import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { SERVER_STRIP_CACHE_MS, type OwnerServerStripDto, type OwnerServerStripRowDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { relative } from '@/lib/utils';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TotpField, isValidTotp } from './OwnerConfirm';

const QUERY_KEY = ['owner-server-strip'];

export function gigabytes(mb: number | null): string {
  if (mb === null) return '—';
  return `${(mb / 1024).toFixed(mb >= 10_240 ? 0 : 1)} GB`;
}

export function uptime(seconds: number | null): string {
  if (seconds === null) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function lag(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return 'current';
  return uptime(seconds);
}

function diskTone(mb: number | null): string {
  if (mb === null) return 'text-muted-foreground';
  if (mb < 5 * 1024) return 'text-destructive font-medium';
  if (mb < 20 * 1024) return 'text-warning-foreground';
  return '';
}

export function ServerStripCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  const strip = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => get<OwnerServerStripDto>('/owner/server-strip'),
    staleTime: SERVER_STRIP_CACHE_MS,
  });

  const refresh = useMutation({
    mutationFn: () => post<OwnerServerStripDto>('/owner/server-strip/refresh', { totpCode }),
    onSuccess: (sample) => {
      queryClient.setQueryData(QUERY_KEY, sample);
      toast.success(sample.throttled ? 'Sample kept' : 'Sample taken', sample.throttled ? 'A sample was taken less than thirty seconds ago; this is that one.' : undefined);
      setAsking(false);
      setTotpCode('');
    },
    onError: (error) => toast.error('Could not take a sample', describeError(error)),
  });

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Server strip</CardTitle>
          <CardDescription>
            What each facility server last reported about itself. One sample is kept for five minutes
            {strip.data ? `; this one is from ${relative(strip.data.sampledAt)}` : ''}.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAsking(true)}>
          <RefreshCw className="h-3.5 w-3.5" /> Sample now
        </Button>
      </CardHeader>
      <CardContent>
        <DataTable
          rows={strip.data?.rows}
          loading={strip.isLoading}
          refreshing={strip.isFetching}
          error={strip.error}
          rowKey={(row: OwnerServerStripRowDto) => row.tenantId}
          empty="No activated facility server has checked in yet."
          columns={[
            {
              header: 'Facility',
              cell: (row: OwnerServerStripRowDto) => (
                <div>
                  <div className="font-medium">{row.name}</div>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-mono">{row.hostname ?? '—'}</span> {row.runningVersion ?? ''}
                  </div>
                </div>
              ),
            },
            { header: 'Disk free', align: 'right', cell: (row: OwnerServerStripRowDto) => <span className={`tabular ${diskTone(row.diskFreeMb)}`}>{gigabytes(row.diskFreeMb)}</span> },
            { header: 'Database', align: 'right', cell: (row: OwnerServerStripRowDto) => <span className="tabular">{gigabytes(row.dbSizeMb)}</span> },
            { header: 'API uptime', align: 'right', cell: (row: OwnerServerStripRowDto) => <span className="tabular">{uptime(row.apiUptimeSec)}</span> },
            { header: 'Last check-in', cell: (row: OwnerServerStripRowDto) => <span className="text-xs">{row.lastCheckInAt ? relative(row.lastCheckInAt) : 'Never'}</span> },
            { header: 'Sync lag', align: 'right', cell: (row: OwnerServerStripRowDto) => <span className="tabular">{lag(row.syncLagSec)}</span> },
            { header: 'Waiting', align: 'right', cell: (row: OwnerServerStripRowDto) => <span className="tabular">{row.outboxBacklog ?? '—'}</span> },
          ]}
        />
      </CardContent>
      {asking && (
        <Dialog open onClose={() => setAsking(false)} title="Take a fresh sample" description="At most one forced sample every thirty seconds; everyone looking at this page shares it.">
          <div className="space-y-4">
            <TotpField value={totpCode} onChange={setTotpCode} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAsking(false)}>
                Cancel
              </Button>
              <Button disabled={!isValidTotp(totpCode)} loading={refresh.isPending} onClick={() => refresh.mutate()}>
                Sample now
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </Card>
  );
}
